import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  UnreadDmItem,
  WelcomeNotificationItem,
  WorkoutAssignedItem,
  BuddyRequestItem,
  BuddyWorkoutItem,
  NewUserItem,
  FollowRequestItem,
} from './notification-bell/NotificationItem';
import { surfaceFreshNotificationToast } from './notification-bell/notificationToasts';

const BUDDY_WORKOUT_TYPES = ['buddy_workout_invite', 'buddy_workout_accepted', 'buddy_workout_completed', 'buddy_session_ended'];

export const NotificationBell = () => {
  const { api } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [followRequestCount, setFollowRequestCount] = useState(0);
  const [unreadDmCount, setUnreadDmCount] = useState(0);
  const [processingId, setProcessingId] = useState(null);
  const dropdownRef = useRef(null);
  const seenNotifIdsRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api().get('/social/notifications');
      const list = res.data.notifications || [];
      if (seenNotifIdsRef.current === null) {
        seenNotifIdsRef.current = new Set(list.map((n) => n.id));
      } else {
        const previouslySeen = seenNotifIdsRef.current;
        const fresh = list.filter((n) => !previouslySeen.has(n.id));
        for (const n of fresh) surfaceFreshNotificationToast(n);
        seenNotifIdsRef.current = new Set(list.map((n) => n.id));
      }
      setNotifications(list);
      setFollowRequestCount(res.data.follow_request_count || 0);
      setUnreadDmCount(res.data.unread_dm_count || 0);
    } catch {
      // silent
    }
  };

  const handleAcceptRequest = async (requestId, userName) => {
    setProcessingId(requestId);
    try {
      await api().post(`/social/follow-requests/${requestId}/accept`);
      toast.success(`Accepted ${userName}'s follow request`);
      fetchNotifications();
    } catch {
      toast.error('Failed to accept request');
    }
    setProcessingId(null);
  };

  const handleRejectRequest = async (requestId, userName) => {
    setProcessingId(requestId);
    try {
      await api().post(`/social/follow-requests/${requestId}/reject`);
      toast.success(`Rejected ${userName}'s request`);
      fetchNotifications();
    } catch {
      toast.error('Failed to reject request');
    }
    setProcessingId(null);
  };

  const handleDismissNotification = async (notifId) => {
    try {
      await api().post(`/social/notifications/${notifId}/dismiss`);
      fetchNotifications();
    } catch {
      // silent
    }
  };

  const handleAcceptWorkoutInvite = async (notif) => {
    setProcessingId(notif.id);
    try {
      await api().post(`/buddies/workout-invite/${notif.invite_id}/accept`);
      toast.success(`Joined ${notif.workout_title || 'the workout'}!`);
      fetchNotifications();
      try { await api().post(`/social/notifications/${notif.id}/dismiss`); } catch { /* silent */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invite');
    }
    setProcessingId(null);
  };

  const handleDeclineWorkoutInvite = async (notif) => {
    setProcessingId(notif.id);
    try {
      await api().post(`/buddies/workout-invite/${notif.invite_id}/decline`);
      toast.success('Invite declined');
      try { await api().post(`/social/notifications/${notif.id}/dismiss`); } catch { /* silent */ }
      fetchNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
    }
    setProcessingId(null);
  };

  const closeDropdown = () => setIsOpen(false);

  const workoutNotifs = notifications.filter((n) => n.type === 'workout_assigned');
  const followNotifs = notifications.filter((n) => n.type === 'follow_request');
  const newUserNotifs = notifications.filter((n) => n.type === 'new_user');
  const welcomeNotifs = notifications.filter((n) => n.type === 'welcome_message');
  const buddyNotifs = notifications.filter((n) => n.type === 'buddy_request');
  const buddyWorkoutNotifs = notifications.filter((n) => BUDDY_WORKOUT_TYPES.includes(n.type));
  const totalCount =
    followRequestCount + workoutNotifs.length + newUserNotifs.length + welcomeNotifs.length +
    buddyNotifs.length + buddyWorkoutNotifs.length + (unreadDmCount > 0 ? 1 : 0);

  const isEmpty =
    unreadDmCount === 0 && workoutNotifs.length === 0 && newUserNotifs.length === 0 &&
    welcomeNotifs.length === 0 && buddyNotifs.length === 0 && buddyWorkoutNotifs.length === 0 &&
    followNotifs.length === 0;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`group relative flex items-center justify-center h-9 w-9 rounded-full transition-all duration-300 ${
          isOpen
            ? 'bg-gradient-to-br from-yellow-300 to-primary text-black shadow-[0_0_14px_rgba(212,175,55,0.6)] scale-105'
            : 'text-white/70 hover:text-primary hover:bg-white/10 active:scale-95'
        }`}
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="h-[18px] w-[18px] transition-transform group-hover:scale-110" strokeWidth={2.2} />
        {totalCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-background shadow-[0_0_8px_rgba(239,68,68,0.6)]">
            {totalCount > 9 ? '9+' : totalCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed sm:absolute right-4 sm:right-0 top-16 sm:top-full sm:mt-2 w-[calc(100vw-2rem)] sm:w-80 max-w-80 bg-card border border-border shadow-2xl z-50"
            data-testid="notification-dropdown"
          >
            <div className="p-4 border-b border-border">
              <h3 className="font-heading text-lg text-white uppercase tracking-wider">Notifications</h3>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {unreadDmCount > 0 && <UnreadDmItem count={unreadDmCount} onClose={closeDropdown} />}

              {welcomeNotifs.map((notif) => (
                <WelcomeNotificationItem
                  key={notif.id}
                  notif={notif}
                  onClick={() => { handleDismissNotification(notif.id); closeDropdown(); }}
                />
              ))}

              {workoutNotifs.map((notif) => (
                <WorkoutAssignedItem
                  key={notif.id}
                  notif={notif}
                  onDismiss={handleDismissNotification}
                  onClose={closeDropdown}
                />
              ))}

              {buddyNotifs.map((notif) => (
                <BuddyRequestItem
                  key={notif.id}
                  notif={notif}
                  onDismiss={handleDismissNotification}
                  onClose={closeDropdown}
                />
              ))}

              {buddyWorkoutNotifs.map((notif) => (
                <BuddyWorkoutItem
                  key={notif.id}
                  notif={notif}
                  processingId={processingId}
                  onAccept={handleAcceptWorkoutInvite}
                  onDecline={handleDeclineWorkoutInvite}
                  onDismiss={handleDismissNotification}
                  onClose={closeDropdown}
                />
              ))}

              {newUserNotifs.map((notif) => (
                <NewUserItem
                  key={notif.id}
                  notif={notif}
                  onDismiss={handleDismissNotification}
                  onClose={closeDropdown}
                />
              ))}

              {followNotifs.map((notif) => (
                <FollowRequestItem
                  key={notif.id}
                  notif={notif}
                  processingId={processingId}
                  onAccept={handleAcceptRequest}
                  onReject={handleRejectRequest}
                  onClose={closeDropdown}
                />
              ))}

              {isEmpty && (
                <div className="p-8 text-center">
                  <div className="h-12 w-12 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Bell className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">No new notifications</p>
                </div>
              )}
            </div>

            {(notifications.length > 0 || unreadDmCount > 0) && (
              <div className="p-3 border-t border-border">
                <Link
                  to="/activity"
                  className="block text-center text-sm text-primary hover:text-primary/80 transition-colors"
                  onClick={closeDropdown}
                  data-testid="view-all-activity-link"
                >
                  View All Activity
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
